import {useEffect, useMemo, useState} from 'react';
import {Link, useNavigate, useParams, useSearchParams} from 'react-router-dom';
import {ArrowLeft, ArrowRight, ArrowUpRight, CalendarBlank, CaretRight, Check, CheckCircle, Clock, MapPin, Sparkle, X} from '@phosphor-icons/react';
import {VStack} from '@astryxdesign/core/VStack';
import {HStack} from '@astryxdesign/core/HStack';
import {Text} from '@astryxdesign/core/Text';
import {Heading} from '@astryxdesign/core/Heading';
import {Button} from '@astryxdesign/core/Button';
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerHeader,
  DrawerTitle,
} from '@/components/ui/drawer';
import {DotGlow} from '@/components/ui/DotGlow';
import {TenantHomePage} from '@/routes/public/TenantHomePage';
import {AsyncBoundary} from '@/components/AsyncBoundary';
import {useTenantContext} from '@/app/TenantContext';
import {useAvailability} from '@/hooks/useAvailability';
import {useCreateBooking} from '@/hooks/useBooking';
import {ContactForm, hasContactErrors, validateContact, type ContactFormErrors} from '@/components/booking/ContactForm';
import {formatMoney} from '@/lib/money';
import {formatZoned, zonedDateKey} from '@/lib/time/tz';
import {clearIdempotencyKey, getIdempotencyKey} from '@/lib/idem';
import {ApiFailure} from '@/lib/api/client';
import {tenantPath} from '@/lib/tenant/resolve';
import {hasStaticDemo} from '@/lib/demo/static';
import {cn} from '@/lib/utils';
import type {ApiBooking, ApiCustomer} from '@/types/api';

type Step = 'intro' | 'service' | 'time' | 'contact' | 'review' | 'success';

const EMPTY_CUSTOMER: ApiCustomer = {name: '', phone: '', car: '', comment: ''};

function formatDuration(minutes: number): string {
  if (minutes < 60) return `${minutes} мин`;
  if (minutes < 1440) return `${Math.round(minutes / 60)} ч`;
  return `${Math.round(minutes / 1440)} д`;
}

function formatCancelWindow(minutes: number): string {
  if (minutes >= 60 && minutes % 60 === 0) return `${minutes / 60} ч`;
  return `${minutes} мин`;
}

export function BookingFlowPage() {
  const {slug = ''} = useParams();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const {tenant, services} = useTenantContext();

  const [serviceId, setServiceId] = useState<string | null>(params.get('service'));
  const presetSlot = params.get('service') ? params.get('slot') : null;
  const [step, setStep] = useState<Step>(presetSlot ? 'contact' : params.get('service') ? 'time' : 'intro');
  const [selectedDate, setSelectedDate] = useState<string>(presetSlot ? zonedDateKey(new Date(presetSlot), tenant.timezone) : '');
  const [startAt, setStartAt] = useState<string | null>(presetSlot);
  const [customer, setCustomer] = useState<ApiCustomer>(EMPTY_CUSTOMER);
  const [showErrors, setShowErrors] = useState(false);
  const [consent, setConsent] = useState(false);
  const [confirmed, setConfirmed] = useState<ApiBooking | null>(null);
  const [token, setToken] = useState<string | null>(null);

  const service = services.find((s) => s.id === serviceId) ?? null;
  const todayKey = useMemo(() => zonedDateKey(new Date(), tenant.timezone), [tenant.timezone]);

  const availability = useAvailability(slug, serviceId, todayKey, 14);
  const createBooking = useCreateBooking();

  useEffect(() => {
    if (availability.data && !selectedDate) {
      const firstOpen = availability.data.days.find((d) => !d.isClosed && d.slots.length > 0);
      setSelectedDate(firstOpen?.date ?? availability.data.days[0]?.date ?? todayKey);
    }
  }, [availability.data, selectedDate, todayKey]);

  const day = availability.data?.days.find((d) => d.date === selectedDate) ?? null;
  const errors: ContactFormErrors = showErrors ? validateContact(customer) : {};
  const endAt = startAt && service ? new Date(new Date(startAt).getTime() + service.durationMinutes * 60_000) : null;

  function close() {
    void navigate(tenantPath(slug, ''));
  }

  function chooseService(id: string) {
    setServiceId(id);
    setSelectedDate('');
    setStartAt(null);
    setStep('time');
  }

  function toReview() {
    setShowErrors(true);
    if (hasContactErrors(validateContact(customer))) return;
    setStep('review');
  }

  async function confirm() {
    if (!serviceId || !startAt) return;
    const scope = `book:${slug}:${serviceId}:${startAt}`;
    const key = getIdempotencyKey(scope);
    try {
      const result = await createBooking.mutateAsync({
        slug,
        serviceId,
        startAt,
        customer,
        idempotencyKey: key,
        demo: tenant.status === 'preview',
      });
      clearIdempotencyKey(scope);
      setConfirmed(result.booking);
      setToken(result.accessToken);
      try {
        localStorage.setItem(`booking-access:${slug}`, result.accessToken);
      } catch {
        /* storage may be unavailable in private mode */
      }
      setStep('success');
    } catch {
      /* error surfaced below */
    }
  }

  const titles: Partial<Record<Step, string>> = {
    service: 'Выберите услугу',
    time: 'Дата и время',
    contact: 'Ваш автомобиль',
    review: 'Проверьте запись',
  };
  const stepNumbers: Partial<Record<Step, number>> = {service: 1, time: 2, contact: 3, review: 4};
  const stepNumber = stepNumbers[step];

  return (
    <>
    <TenantHomePage />
    <Drawer open onOpenChange={(open) => !open && close()} modal swipeDirection="down">
      <DrawerContent>
        <DrawerHeader>
          <HStack gap={2} vAlign="center" hAlign="between">
            <HStack gap={2} vAlign="center">
              <VStack gap={0}>
                <h2 className="m-0 text-[22px] font-bold leading-tight tracking-tight text-foreground">{titles[step] ?? 'Запись в студию'}</h2>
                {stepNumber ? (
                  <span className="text-[14px] text-muted-foreground">Шаг {stepNumber} из 4</span>
                ) : null}
              </VStack>
            </HStack>
<DrawerClose
              aria-label="Закрыть"
              className="flex h-10 w-10 items-center justify-center rounded-full text-foreground transition-colors hover:bg-white/10"
            >
              <X size={24} aria-hidden />
            </DrawerClose>
          </HStack>
          {stepNumber ? (
            <HStack gap={1} aria-hidden>
              {[1, 2, 3, 4].map((i) => (
                <span
                  key={i}
                  className={cn(
                    'h-1 flex-1 rounded-full transition-colors',
                    i <= stepNumber ? 'bg-tenant-accent' : 'bg-white/12',
                  )}
                />
              ))}
            </HStack>
          ) : null}
          <DrawerTitle className="sr-only">Оформление записи</DrawerTitle>
          <DrawerDescription className="sr-only">Выберите услугу, время и оставьте контакты</DrawerDescription>
        </DrawerHeader>

        {step === 'intro' ? (
          <VStack gap={4} paddingInline={1} paddingBlock={2}>
            <VStack gap={3} align="center" className="rounded-3xl panel px-5 py-8">
              <span className="flex h-16 w-16 items-center justify-center rounded-2xl bg-tenant-accent text-on-accent">
                <Sparkle size={32} weight="fill" aria-hidden />
              </span>
              <Heading level={3}>Свежий вид для вашего авто</Heading>
              <Text type="supporting" justify="center">
                Выберите услугу и удобное время — остальное возьмём на себя
              </Text>
            </VStack>
            <Button label="Выбрать время" variant="primary" size="lg" width="100%" icon={<CaretRight size={18} />} onClick={() => setStep('service')} />
          </VStack>
        ) : null}

        {step === 'service' ? (
          <div className="panel mb-4 flex flex-col gap-1 rounded-[28px] p-2">
            {services.length === 0 ? (
              <AsyncBoundary isEmpty emptyTitle="Услуги пока не добавлены" />
            ) : (
              services.map((s) => (
                <button
                  key={s.id}
                  type="button"
                  onClick={() => chooseService(s.id)}
                  className="service-row flex w-full items-center gap-3 rounded-2xl border border-transparent bg-transparent px-4 py-3.5 text-left"
                >
                  <span className="flex min-w-0 flex-1 flex-col gap-1">
                    <span className="text-[16px] font-semibold leading-snug text-foreground">{s.name}</span>
                    {s.description ? (
                      <span className="text-[14px] leading-snug text-muted-foreground">{s.description}</span>
                    ) : null}
                    <span className="text-[14px] text-muted-foreground">{formatDuration(s.durationMinutes)}</span>
                  </span>
                  <span className="flex flex-col items-end gap-1">
                    <span className="text-[16px] font-semibold text-foreground">
                      {formatMoney(s.priceMinor, s.currency, tenant.locale)}
                    </span>
                    <span className="text-[14px] text-muted-foreground">Выбрать</span>
                  </span>
                  <ArrowUpRight size={20} className="shrink-0 text-foreground" aria-hidden />
                </button>
              ))
            )}
          </div>
        ) : null}

        {step === 'time' && service ? (
          <VStack gap={4}>
            <HStack gap={2} vAlign="center" hAlign="between">
              <Text type="body" weight="semibold">
                {service.name}
              </Text>
              <Text type="body" weight="semibold" hasTabularNumbers>
                {formatMoney(service.priceMinor, service.currency, tenant.locale)}
              </Text>
            </HStack>

            <div className="grid grid-cols-7 gap-2">
              {(availability.data?.days ?? []).slice(0, 7).map((d) => {
                const date = new Date(`${d.date}T12:00:00Z`);
                const disabled = d.isClosed || d.slots.length === 0;
                const active = d.date === selectedDate;
                const weekday = new Intl.DateTimeFormat('ru-RU', {weekday: 'short', timeZone: tenant.timezone}).format(date);
                return (
                  <button
                    key={d.date}
                    type="button"
                    disabled={disabled}
                    onClick={() => {
                      setSelectedDate(d.date);
                      setStartAt(null);
                    }}
                    className={cn(
                      'flex h-[72px] flex-col items-center justify-center gap-1 rounded-2xl border text-center transition-colors',
                      active ? 'border-transparent btn-accent' : 'border-white/10 bg-[#111113] text-foreground',
                      disabled && 'opacity-40',
                    )}
                  >
                    <span className={cn('text-[14px]', active ? '' : 'text-muted-foreground')}>{weekday}</span>
                    <span className="text-[18px] font-semibold leading-none">{formatZoned(date, 'd', tenant.timezone)}</span>
                  </button>
                );
              })}
            </div>

            <VStack gap={1}>
              <span className="text-[15px] text-muted-foreground">Другая дата</span>
              <label className="relative flex h-12 items-center justify-between overflow-hidden rounded-2xl border border-white/10 bg-[#111113] px-4">
                <span className="text-[16px] text-foreground">
                  {selectedDate ? formatZoned(new Date(`${selectedDate}T12:00:00Z`), 'dd.MM.yyyy', tenant.timezone) : ''}
                </span>
                <CalendarBlank size={20} className="text-foreground" aria-hidden />
                <input
                  type="date"
                  aria-label="Другая дата"
                  min={todayKey}
                  value={selectedDate}
                  onChange={(e) => {
                    if (e.target.value) {
                      setSelectedDate(e.target.value);
                      setStartAt(null);
                    }
                  }}
                  className="absolute inset-0 cursor-pointer opacity-0"
                />
              </label>
            </VStack>

            <AsyncBoundary
              isLoading={availability.isLoading}
              error={availability.error}
              onRetry={() => void availability.refetch()}
              isEmpty={!availability.isLoading && (day?.slots.length ?? 0) === 0}
              emptyTitle="На этот день свободных окон нет"
              emptyHint="Выберите другой день."
            >
              <HStack gap={2} wrap="wrap">
                {(day?.slots ?? []).map((slot) => {
                  const active = slot === startAt;
                  return (
                    <button
                      key={slot}
                      type="button"
                      onClick={() => setStartAt(slot)}
                      className={cn(
                        'rounded-full border px-4 py-2 text-[15px] font-semibold transition-colors',
                        active ? 'border-transparent btn-accent' : 'border-white/10 bg-[#111113] text-foreground',
                      )}
                    >
                      <Text type="body" weight="medium" hasTabularNumbers>
                        {formatZoned(new Date(slot), 'HH:mm', tenant.timezone)}
                      </Text>
                    </button>
                  );
                })}
              </HStack>
            </AsyncBoundary>

            <button
              type="button"
              disabled={!startAt}
              onClick={() => setStep('contact')}
              className="btn-accent flex items-center justify-center gap-3 rounded-full px-6 py-3.5 text-[16px] font-semibold disabled:opacity-45"
            >
              Продолжить
              <ArrowRight size={20} aria-hidden />
            </button>
            <button
              type="button"
              onClick={() => setStep('service')}
              className="flex items-center justify-center gap-2 py-1 text-[16px] font-semibold text-foreground"
            >
              <ArrowLeft size={20} aria-hidden />
              Назад
            </button>
          </VStack>
        ) : null}

        {step === 'contact' && service && startAt ? (
          <div className="flex flex-col gap-5 pb-2">
            <ContactForm value={customer} onChange={setCustomer} errors={errors} showErrors={showErrors} />
            {createBooking.error ? (
              <p role="alert" className="m-0 text-[14px] text-red-300">
                {createBooking.error instanceof ApiFailure ? createBooking.error.message : 'Не удалось создать запись. Попробуйте другое время.'}
              </p>
            ) : null}
            <button
              type="button"
              onClick={toReview}
              className="btn-accent flex items-center justify-center gap-3 rounded-full px-6 py-3.5 text-[16px] font-semibold"
            >
              Проверить запись
              <ArrowRight size={20} aria-hidden />
            </button>
            <button
              type="button"
              onClick={() => setStep('time')}
              className="flex items-center justify-center gap-2 py-1 text-[15px] font-semibold text-foreground"
            >
              <ArrowLeft size={20} aria-hidden />
              Назад
            </button>
          </div>
        ) : null}

        {step === 'review' && service && startAt && endAt ? (
          <div className="flex flex-col gap-5 pb-2">
            <div className="panel flex flex-col gap-4 rounded-3xl p-5">
              <h3 className="m-0 text-[22px] font-bold leading-tight tracking-tight text-foreground">{service.name}</h3>
              <div className="flex flex-col gap-3 text-[15px]">
                <div className="flex items-baseline justify-between gap-3">
                  <span className="text-muted-foreground">Заезд</span>
                  <span className="font-semibold text-foreground">{formatZoned(new Date(startAt), 'd MMMM в HH:mm', tenant.timezone)}</span>
                </div>
                <div className="flex items-baseline justify-between gap-3">
                  <span className="text-muted-foreground">Выдача, ориентир</span>
                  <span className="font-semibold text-foreground">{formatZoned(endAt, 'd MMMM в HH:mm', tenant.timezone)}</span>
                </div>
                <div className="flex items-baseline justify-between gap-3">
                  <span className="text-muted-foreground">Стоимость</span>
                  <span className="font-semibold text-foreground">{formatMoney(service.priceMinor, service.currency, tenant.locale)}</span>
                </div>
              </div>
              <div className="flex flex-col gap-2 text-[15px] text-foreground">
                <span className="font-semibold">{customer.car ? `${customer.name} / ${customer.car}` : customer.name}</span>
                <span className="text-muted-foreground">{customer.phone}</span>
                {tenant.address ? <span className="text-muted-foreground">{tenant.address}</span> : null}
              </div>
            </div>
            <p className="m-0 text-[14px] leading-relaxed text-muted-foreground">
              Отмена онлайн не позднее чем за {formatCancelWindow(tenant.cancelWindowMinutes)} до заезда. Время студии: {tenant.timezone}.
            </p>
            <div className="flex items-start gap-3 text-left">
              <button
                type="button"
                role="checkbox"
                aria-checked={consent}
                aria-label="Согласие на обработку персональных данных"
                onClick={() => setConsent((v) => !v)}
                className={cn(
                  'flex h-6 w-6 shrink-0 items-center justify-center rounded-[7px] border',
                  consent ? 'border-transparent btn-accent' : 'border-white/20 bg-[#1a1a1c]',
                )}
              >
                {consent ? <Check size={16} weight="bold" aria-hidden /> : null}
              </button>
              <span className="text-[14px] leading-snug text-foreground">
                Даю согласие на обработку персональных данных и их хранение на сервере в США для оформления записи.{' '}
                <Link to={tenantPath(slug, 'privacy')} target="_blank" rel="noopener" className="underline">
                  Политика конфиденциальности
                </Link>
              </span>
            </div>
            {createBooking.error ? (
              <p role="alert" className="m-0 text-[14px] text-red-300">
                {createBooking.error instanceof ApiFailure ? createBooking.error.message : 'Не удалось создать запись.'}
              </p>
            ) : null}
            <button
              type="button"
              disabled={!consent || createBooking.isPending}
              onClick={() => void confirm()}
              className="btn-accent flex items-center justify-center gap-3 rounded-full px-6 py-3.5 text-[16px] font-semibold disabled:opacity-45"
            >
              {createBooking.isPending ? 'Создаём запись…' : 'Подтвердить запись'}
              <Check size={20} aria-hidden />
            </button>
            <button
              type="button"
              onClick={() => setStep('contact')}
              className="flex items-center justify-center gap-2 py-1 text-[15px] font-semibold text-foreground"
            >
              <ArrowLeft size={20} aria-hidden />
              Назад
            </button>
          </div>
        ) : null}

        {step === 'success' && confirmed ? (
          <div className="flex flex-col gap-5 pb-2">
            <div className="flex flex-col items-center gap-3 px-2 pt-2 text-center">
              <CheckCircle size={56} weight="regular" className="text-foreground" aria-hidden />
              <h3 className="m-0 text-[26px] font-bold leading-tight tracking-tight text-foreground">Вы записаны</h3>
              <p className="m-0 text-[15px] leading-relaxed text-muted-foreground">
                Мы ждём вас. Сохраните ссылку на запись: по ней можно перенести или отменить визит.
              </p>
              {hasStaticDemo(slug) ? (
                <p className="m-0 text-[14px] leading-snug text-muted-foreground">
                  Это демонстрационная версия: запись студии не отправлена.
                </p>
              ) : null}
            </div>
            <div className="panel flex flex-col gap-3 rounded-3xl p-5 text-[15px] text-foreground">
              <div className="flex items-center gap-3">
                <Clock size={20} aria-hidden />
                <span className="font-semibold">{formatZoned(new Date(confirmed.startAt), 'd MMM, HH:mm', tenant.timezone)}</span>
              </div>
              {(confirmed.address ?? tenant.address) ? (
                <div className="flex items-center gap-3">
                  <MapPin size={20} aria-hidden />
                  <span>{confirmed.address ?? tenant.address}</span>
                </div>
              ) : null}
              <span className="border-t border-white/10 pt-3 text-muted-foreground">
                {confirmed.serviceName} · {formatMoney(confirmed.priceMinor, confirmed.currency, tenant.locale)}
              </span>
            </div>
            <div className="panel panel-glow flex flex-col gap-4 rounded-3xl p-5">
              <DotGlow seed={9} />
              <div className="flex flex-col gap-1">
                <span className="text-[20px] font-bold leading-tight text-foreground">Студия всегда под рукой</span>
                <span className="text-[14px] leading-snug text-muted-foreground">Записывайтесь за несколько касаний, без поиска ссылки и звонков</span>
              </div>
              <Link to={tenantPath(slug, 'install')} className="btn-accent flex items-center justify-center gap-3 rounded-full px-6 py-3.5 text-[15px] font-semibold">
                Добавить на экран
                <ArrowRight size={20} aria-hidden />
              </Link>
              <span className="text-[14px] text-muted-foreground">Бесплатно. Установка через браузер.</span>
            </div>
            <button
              type="button"
              onClick={() => void navigate(tenantPath(slug, `booking/${token ?? confirmed.id}`))}
              className="btn-accent rounded-full px-6 py-3.5 text-[16px] font-semibold"
            >
              Открыть мою запись
            </button>
            <button
              type="button"
              onClick={() => void navigate(tenantPath(slug))}
              className="rounded-full border border-white/15 px-6 py-3.5 text-[15px] font-semibold text-foreground"
            >
              На главную
            </button>
          </div>
        ) : null}
      </DrawerContent>
    </Drawer>
    </>
  );
}